import React, { useState } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Grid, 
  Link, 
  Paper, 
  InputAdornment,
  IconButton,
  useTheme,
  Alert
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { loginUser, getUserProfile, UserProfile } from '../firebase/services';
import { useLanguage } from '../context/LanguageContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const theme = useTheme();

  // Helper function to get string value from translation
  const getTranslationString = (key: string, defaultValue: string = ''): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : defaultValue;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Basic validation
    if (!email.trim() || !password.trim()) {
      setError(getTranslationString('login.errorRequiredFields', 'Please enter both email and password'));
      return;
    }

    try {
      setLoading(true);
      const user = await loginUser(email, password);
      
      // Get user role from users collection
      const userData = await getUserProfile(user.uid);
      
      // Redirect based on role
      if (userData && userData.roleId === 1) {
        navigate('/dashboard/admin');
      } else {
        navigate('/dashboard/client');
      }
    } catch (err: any) {
      setError(err.message || getTranslationString('login.errorInvalidCredentials', 'Failed to login. Please check your credentials.'));
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box 
      sx={{ 
        py: { xs: 4, md: 8 },
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background pattern */}
      <Box 
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
          opacity: 0.03,
          background: `radial-gradient(circle at 25% 25%, ${theme.palette.primary.main} 0%, transparent 25%),
                       radial-gradient(circle at 75% 75%, ${theme.palette.secondary.main} 0%, transparent 25%)`,
        }}
      />

      <Container maxWidth="sm">
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderTop: '4px solid #015480',
            borderLeft: 'none',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
          }}
        >
          <Typography 
            component="h1" 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              mb: 1,
              background: 'linear-gradient(90deg, #015480, #0288d1)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'center',
              fontFamily: '"Orbitron", "Rajdhani", sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            {getTranslationString('login.title')}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 3, 
              textAlign: 'center',
              fontFamily: '"Inter", "Roboto", sans-serif',
              fontSize: '0.9rem'
            }}
          >
            {getTranslationString('login.description')}
          </Typography>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                width: '100%', 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: 'error.main'
                }
              }}
            >
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ 
            width: '100%',
            position: 'relative'
          }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={getTranslationString('login.email')}
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover fieldset': {
                    borderColor: '#0288d1',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#015480',
                    borderWidth: '2px',
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#015480',
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={getTranslationString('login.password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover fieldset': {
                    borderColor: '#0288d1',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#015480',
                    borderWidth: '2px',
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#015480',
                }
              }}
            />
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 1,
              mb: 3
            }}>
              <Link 
                component={RouterLink} 
                to="/forgot-password" 
                variant="body2"
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {getTranslationString('login.forgotPassword')}
              </Link>
            </Box>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                boxShadow: 'none',
                background: 'linear-gradient(90deg, #015480, #0288d1)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(2, 136, 209, 0.3)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {loading ? getTranslationString('login.loading', 'Logging in...') : getTranslationString('login.submit', 'Login')}
            </Button>
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to="/register"
                variant="body2"
                sx={{ 
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {getTranslationString('login.noAccount')}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login; 